import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, HERO_IMG, CITY_IMG, LOGISTICS_IMG } from "../lib/api";
import VinSearch from "../components/VinSearch";
import PartCard from "../components/PartCard";
import {
  Cog, Zap, Disc, Filter, Car, Wrench, Flame, Gauge, Lightbulb,
  ShieldCheck, Plane, Ship, ArrowRight, MapPin, Timer,
} from "lucide-react";

const CATEGORY_ICONS = {
  Brakes: Disc, Engine: Cog, Filters: Filter, Electrical: Zap,
  Body: Car, Transmission: Wrench, Suspension: Gauge, Cooling: Flame,
  Exhaust: Flame, Steering: Gauge, "Fuel System": Flame, Lighting: Lightbulb,
};

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/parts/featured", { params: { limit: 8 } }).then(r => setFeatured(r.data));
    api.get("/catalog/categories").then(r => setCategories(r.data));
  }, []);

  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#121212] text-white">
        <div
          className="absolute inset-0 opacity-40"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/70 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3 fade-up">
            <div className="inline-flex items-center gap-2 bg-[#FFD100] text-[#121212] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest mb-6">
              <span className="w-2 h-2 bg-[#009A44] inline-block" /> Trusted across 10+ African markets
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight mb-6">
              Every part, <br />
              <span className="text-[#FFD100]">every vehicle</span>, <br />
              sourced for Africa.
            </h1>
            <p className="text-lg text-zinc-300 max-w-xl mb-8 leading-relaxed">
              Mechanics and car owners across the continent find, compare, and purchase
              the exact auto parts they need. Agent-verified. Globally sourced. Delivered
              by air or sea.
            </p>
            <div className="flex flex-wrap gap-6 text-sm">
              <Stat value="250+" label="Parts in stock" />
              <Stat value="15+" label="Vehicle makes" />
              <Stat value="48h" label="Availability lock" />
              <Stat value="10" label="Countries served" />
            </div>
          </div>
          <div className="lg:col-span-2">
            <VinSearch large />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <div className="grid md:grid-cols-4 gap-6">
          <Feature
            icon={<ShieldCheck size={28} className="text-[#009A44]" />}
            title="Agent verified"
            text="Local agents physically inspect parts before shipment. No fakes, no surprises."
          />
          <Feature
            icon={<Timer size={28} className="text-[#EF3340]" />}
            title="48-hour hold"
            text="Payment is held for up to 48 hours until full availability is confirmed."
          />
          <Feature
            icon={<Plane size={28} className="text-[#121212]" />}
            title="Air + Sea freight"
            text="Choose rush air freight or cost-effective sea. Customs clearance handled."
          />
          <Feature
            icon={<MapPin size={28} className="text-[#FFD100]" />}
            title="Last-mile delivery"
            text="Local agents handle clearance and drop-off from Lagos to Mombasa."
          />
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-zinc-50 border-y border-[#E4E4E7] apa-pattern">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-[#009A44] mb-2">Browse</div>
              <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Shop by category</h2>
            </div>
            <Link to="/search" className="text-sm font-semibold uppercase tracking-wider hover:text-[#009A44] inline-flex items-center gap-2">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="category-grid">
            {categories.map((c) => {
              const Icon = CATEGORY_ICONS[c] || Cog;
              return (
                <Link
                  key={c}
                  to={`/search?category=${encodeURIComponent(c)}`}
                  className="group bg-white p-5 border border-[#E4E4E7] hover:border-[#121212] hover:shadow-md transition-all hover:-translate-y-1"
                  data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-12 h-12 bg-[#121212] text-white flex items-center justify-center mb-4 group-hover:bg-[#009A44] transition-colors">
                    <Icon size={22} />
                  </div>
                  <div className="font-display font-bold text-base">{c}</div>
                  <div className="text-xs text-zinc-500 mt-1">Shop {c.toLowerCase()}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PARTS */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#EF3340] mb-2">Top rated</div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Featured parts</h2>
          </div>
          <Link to="/search" className="text-sm font-semibold uppercase tracking-wider hover:text-[#009A44] inline-flex items-center gap-2">
            Browse inventory <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="featured-grid">
          {featured.map(p => <PartCard key={p.part_id} part={p} />)}
        </div>
      </section>

      {/* LOGISTICS */}
      <section className="bg-[#121212] text-white">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#FFD100] mb-3">Logistics</div>
            <h2 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight mb-6">
              End-to-end fulfillment built for African roads.
            </h2>
            <p className="text-zinc-300 text-lg mb-8 leading-relaxed max-w-xl">
              Our courier, cargo and airline partnerships coordinate air or sea freight,
              customs clearance through local agents, and last-mile delivery — all inside
              a single dashboard.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8 max-w-md">
              <div className="border border-white/15 p-4">
                <Plane size={22} className="text-[#FFD100] mb-2" />
                <div className="font-bold">Air freight</div>
                <div className="text-xs text-zinc-400 mt-1">3–7 days</div>
              </div>
              <div className="border border-white/15 p-4">
                <Ship size={22} className="text-[#FFD100] mb-2" />
                <div className="font-bold">Sea freight</div>
                <div className="text-xs text-zinc-400 mt-1">21–45 days</div>
              </div>
            </div>
            <Link to="/search" className="btn-primary" data-testid="cta-logistics-shop">
              Start shopping <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <img src={LOGISTICS_IMG} alt="Cargo containers" className="aspect-[3/4] w-full object-cover" />
            <img src={CITY_IMG} alt="African city" className="aspect-[3/4] w-full object-cover mt-10" />
          </div>
        </div>
      </section>

      {/* BECOME */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 py-20 grid md:grid-cols-3 gap-4">
        <BecomeCard
          color="#009A44"
          title="Sell on APA"
          text="List your inventory. Agent verification unlocks trust. We handle logistics."
          cta="Become a seller"
          to="/register?role=seller"
          testid="become-seller"
        />
        <BecomeCard
          color="#FFD100"
          title="Join as an agent"
          text="Earn by verifying parts and users in your city. Be the trust layer for your region."
          cta="Join as agent"
          to="/register?role=local_agent"
          testid="become-agent"
          darkText
        />
        <BecomeCard
          color="#EF3340"
          title="Mechanic program"
          text="Bulk pricing, priority support, and a pro dashboard built for busy workshops."
          cta="Apply as mechanic"
          to="/register?role=mechanic"
          testid="become-mechanic"
        />
      </section>
    </main>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display text-3xl font-extrabold">{value}</div>
      <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{label}</div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="p-6 border border-[#E4E4E7] bg-white">
      <div className="mb-4">{icon}</div>
      <h3 className="font-display font-bold text-xl mb-2">{title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed">{text}</p>
    </div>
  );
}

function BecomeCard({ color, title, text, cta, to, testid, darkText }) {
  const textColor = darkText ? "#121212" : "#ffffff";
  return (
    <Link
      to={to}
      className="block p-8 transition-all hover:-translate-y-1 hover:shadow-xl"
      style={{ background: color, color: textColor }}
      data-testid={testid}
    >
      <h3 className="font-display text-3xl font-extrabold mb-3">{title}</h3>
      <p className="text-sm opacity-90 mb-6 leading-relaxed max-w-sm">{text}</p>
      <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-b-2" style={{ borderColor: textColor }}>
        {cta} <ArrowRight size={16} />
      </div>
    </Link>
  );
}
